import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * PaymentInputRow - Isolated payment input component
 * Maintains its own state to prevent re-renders from clearing input values
 * This solves the issue where background polling causes inputs to lose focus/values
 */
function PaymentInputRow({ 
  orderId, 
  paymentMethod, 
  grandTotal, 
  currentPaid, 
  onRecordPayment,
  isCredit 
}) {
  // Local state for input values - survives parent re-renders
  const [cashValue, setCashValue] = useState('');
  const [bankValue, setBankValue] = useState('');
  const [creditValue, setCreditValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Refs to maintain focus
  const cashInputRef = useRef(null);
  const bankInputRef = useRef(null);
  const creditInputRef = useRef(null);
  const activeInputRef = useRef(null);
  
  // Track which input had focus before any re-render
  const handleFocus = useCallback((inputName) => {
    activeInputRef.current = inputName;
  }, []);
  
  // Restore focus after render if needed
  useEffect(() => {
    if (activeInputRef.current) {
      const inputMap = {
        'cash': cashInputRef,
        'bank': bankInputRef,
        'credit': creditInputRef
      };
      const targetRef = inputMap[activeInputRef.current];
      if (targetRef?.current && document.activeElement !== targetRef.current) {
        requestAnimationFrame(() => {
          targetRef.current?.focus();
        });
      }
    }
  });

  const handleRecordPayment = useCallback(async () => {
    const cashAmount = parseFloat(cashValue || 0) || 0;
    const bankAmount = parseFloat(bankValue || 0) || 0;
    const creditAmount = parseFloat(creditValue || 0) || 0;
    
    // Validate at least one payment entered
    if (cashAmount <= 0 && bankAmount <= 0 && creditAmount <= 0) {
      alert('Please enter at least one payment amount');
      return;
    }
    
    const remainingBalance = Math.max(0, grandTotal - currentPaid);
    const totalPaymentAttempt = cashAmount + bankAmount + creditAmount;
    
    // Validate not overpaying
    if (totalPaymentAttempt > remainingBalance + 0.01) {
      alert(`Payment exceeds remaining balance.\n\nRemaining Balance: £${remainingBalance.toFixed(2)}\nYou tried to pay: £${totalPaymentAttempt.toFixed(2)}\nOverpayment: £${(totalPaymentAttempt - remainingBalance).toFixed(2)}`);
      return;
    }
    
    // Build confirmation message
    const confirmBreakdown = [];
    if (cashAmount > 0) confirmBreakdown.push(`£${cashAmount.toFixed(2)} as CASH`);
    if (bankAmount > 0) confirmBreakdown.push(`£${bankAmount.toFixed(2)} as BANK`);
    if (creditAmount > 0) confirmBreakdown.push(`£${creditAmount.toFixed(2)} as CREDIT`);
    
    const confirmMessage = `Are you sure you want to enter:\n\n${confirmBreakdown.join('\n')}\n\nTotal Payment: £${totalPaymentAttempt.toFixed(2)}\nRemaining Balance After: £${(remainingBalance - totalPaymentAttempt).toFixed(2)}`;
    
    if (!confirm(confirmMessage)) {
      return;
    }
    
    setIsProcessing(true);
    
    try {
      await onRecordPayment({
        cashAmount,
        bankAmount,
        creditAmount
      });
      
      // Clear inputs after successful payment
      setCashValue('');
      setBankValue('');
      setCreditValue('');
      activeInputRef.current = null;
    } catch (err) {
      console.error('Failed to record payment:', err);
      alert('Failed to record payment: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsProcessing(false);
    }
  }, [cashValue, bankValue, creditValue, grandTotal, currentPaid, onRecordPayment]);

  return (
    <div style={{
      display: 'flex', 
      flexDirection: 'column', 
      gap: '6px', 
      padding: '10px 8px', 
      backgroundColor: '#f8fafc', 
      borderRadius: '8px', 
      border: '1px solid #e2e8f0'
    }}>
      {/* Cash Payment Input */}
      <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
        <label style={{width:'52px', fontSize:'12px', fontWeight:'600', color:'#16a34a'}}>Cash:</label>
        <input 
          ref={cashInputRef}
          type="number" 
          value={cashValue}
          onChange={(e) => setCashValue(e.target.value)}
          onFocus={() => handleFocus('cash')}
          onBlur={() => { /* Don't clear activeInputRef on blur to handle click delays */ }}
          className="form-control" 
          style={{
            width: '90px', 
            fontSize:'12px', 
            padding:'5px 8px',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            backgroundColor: '#fafbfc'
          }}
          placeholder="0.00"
          step="0.01"
          min="0"
          disabled={isProcessing}
        />
      </div>
      
      {/* Bank Payment Input */}
      <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
        <label style={{width:'52px', fontSize:'12px', fontWeight:'600', color:'#2563eb'}}>Bank:</label>
        <input 
          ref={bankInputRef}
          type="number" 
          value={bankValue}
          onChange={(e) => setBankValue(e.target.value)}
          onFocus={() => handleFocus('bank')}
          className="form-control" 
          style={{
            width: '90px', 
            fontSize:'12px', 
            padding:'5px 8px',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            backgroundColor: '#fafbfc'
          }}
          placeholder="0.00"
          step="0.01"
          min="0"
          disabled={isProcessing}
        />
      </div>
      
      {/* Credit Payment Input (only for credit orders) */}
      {isCredit && (
        <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
          <label style={{width:'52px', fontSize:'12px', fontWeight:'600', color:'#f59e0b'}}>Credit:</label>
          <input 
            ref={creditInputRef}
            type="number" 
            value={creditValue}
            onChange={(e) => setCreditValue(e.target.value)}
            onFocus={() => handleFocus('credit')}
            className="form-control" 
            style={{
              width: '90px', 
              fontSize:'12px', 
              padding:'5px 8px',
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              backgroundColor: '#fafbfc'
            }}
            placeholder="0.00"
            step="0.01"
            min="0"
            disabled={isProcessing}
          />
        </div>
      )}
      
      {/* Record Payment Button */}
      <div style={{display:'flex', gap:'6px', marginTop:'3px'}}>
        <button 
          onClick={handleRecordPayment}
          disabled={isProcessing}
          style={{
            fontSize:'13px', 
            padding: '8px 14px', 
            whiteSpace:'nowrap', 
            fontWeight:'500',
            borderRadius: '6px',
            backgroundColor: isProcessing ? '#94a3b8' : '#2563eb',
            border: 'none',
            color: 'white',
            transition: 'all 0.2s ease',
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            opacity: isProcessing ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!isProcessing) {
              e.target.style.opacity = '0.9';
              e.target.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = isProcessing ? '0.7' : '1';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          {isProcessing ? '⏳ Processing...' : '💰 Record Payment'}
        </button>
      </div>
    </div>
  );
}

// Use React.memo to prevent unnecessary re-renders when parent updates
export default React.memo(PaymentInputRow, (prevProps, nextProps) => {
  // Only re-render if these key props change
  return prevProps.orderId === nextProps.orderId &&
         prevProps.grandTotal === nextProps.grandTotal &&
         prevProps.currentPaid === nextProps.currentPaid &&
         prevProps.paymentMethod === nextProps.paymentMethod;
});
